/**
 * BOM grouped by Subsystem — Phase 10
 * Card-per-subsystem layout: CCTV, Data, WAP, Fire Alarm, etc.
 * Each card has its own totals, item list, and export button.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Layers, Download, Loader2, DollarSign, ChevronDown, ChevronUp,
} from "lucide-react";
import api from "@/lib/api";

const fmt = new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
});

export default function BomBySubsystem({ estimationId }) {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    if (!estimationId) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get(`/estimations/${estimationId}/bom-by-subsystem`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [estimationId]);

  const toggle = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const exportCsv = async (key) => {
    const res = await api.get(
      `/estimations/${estimationId}/export/subsystem-csv?subsystem=${key}`,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${key}-${estimationId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center p-12">
      <Loader2 className="animate-spin text-[#003366]" size={20} />
    </div>
  );
  if (!data || !data.groups.length) return (
    <div className="iqes-card p-8 text-center text-sm text-slate-500" data-testid="bom-subsystem-empty">
      {es ? "Sin items en el BOM aún. Sube un plano o usa el Count Tool."
          : "No BOM items yet. Upload a plan or use the Count Tool."}
    </div>
  );

  return (
    <div data-testid="bom-by-subsystem">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Layers size={16} /> {es ? "BOM por Subsistema" : "BOM by Subsystem"}
        </h3>
        <div className="text-sm font-mono text-slate-700">
          {data.groups.length} {es ? "subsistemas" : "subsystems"} ·{" "}
          <span className="font-semibold text-[#003366]">
            {fmt.format(data.grand_totals.subtotal)}
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {data.groups.map(g => {
          const isOpen = expanded[g.subsystem];
          return (
            <div key={g.subsystem}
              className="iqes-card overflow-hidden"
              data-testid={`subsystem-card-${g.subsystem}`}>
              <button
                onClick={() => toggle(g.subsystem)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color }} />
                  <div className="text-left">
                    <div className="font-semibold text-slate-900">
                      {es ? g.label_es : g.label_en}
                    </div>
                    <div className="text-xs font-mono text-slate-500">
                      {g.totals.count} items · {fmt.format(g.totals.subtotal)}
                    </div>
                  </div>
                </div>
                {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
              </button>

              {isOpen && (
                <div className="border-t border-slate-100">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                        <th className="text-left p-2">{es ? "Item" : "Item"}</th>
                        <th className="text-right p-2">Qty</th>
                        <th className="text-right p-2">$</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((it, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="p-2">
                            <div className="font-medium text-slate-900">{it.label}</div>
                            {it.is_manual && <span className="text-[9px] font-mono text-amber-600">·MANUAL</span>}
                          </td>
                          <td className="p-2 text-right font-mono text-slate-700">{it.qty} {it.unit}</td>
                          <td className="p-2 text-right font-mono font-semibold text-slate-900">{fmt.format(it.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-50 border-t-2 border-slate-300">
                        <td className="p-2 text-[10px] font-mono uppercase tracking-wider text-slate-700">TOTAL</td>
                        <td className="p-2 text-right font-mono text-slate-700">{g.totals.count}</td>
                        <td className="p-2 text-right font-mono font-semibold text-slate-900">{fmt.format(g.totals.subtotal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="p-2 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button
                      onClick={() => exportCsv(g.subsystem)}
                      data-testid={`export-subsystem-${g.subsystem}`}
                      className="text-[10px] font-mono uppercase tracking-wider text-[#003366] hover:underline inline-flex items-center gap-1">
                      <Download size={10} /> {es ? "CSV solo" : "CSV only"} {g.label_en}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="iqes-card-soft mt-4 p-4 flex items-center justify-between bg-[#003366]/5 border-[#003366]/20">
        <div className="flex items-center gap-2">
          <DollarSign size={16} className="text-[#003366]" />
          <div className="text-xs font-mono uppercase tracking-wider text-[#003366]">
            {es ? "Total general" : "Grand total"}
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-lg font-semibold text-[#003366]">
            {fmt.format(data.grand_totals.subtotal)}
          </div>
          <div className="text-[10px] font-mono text-slate-500">
            Mat {fmt.format(data.grand_totals.material)} · Lab {fmt.format(data.grand_totals.labor)} · {data.grand_totals.count} items
          </div>
        </div>
      </div>
    </div>
  );
}
